import React, { lazy, Suspense } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguageCodes, defaultLanguage } from '../i18n/config';

// Lazy load reviews badge
const FooterReviewsBadge = lazy(() => import('./GoogleReviews').then(m => ({ default: m.FooterReviewsBadge })));

// Inline SVG icons
const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 mt-0.5 flex-shrink-0">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/>
  </svg>
);
const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 flex-shrink-0">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 flex-shrink-0">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const SmartphoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 flex-shrink-0">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
);

const Footer = () => {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  
  // Get current language from URL
  const currentLang = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
  const localizedPath = (path) => `/${currentLang}${path}`;

  const experienceLinks = [
    { label: 'Happy Hour Sunset Tour', to: '/experience/happy-hour-sunset-tour' },
    { label: 'Sfoglia & Tortellini Experience', to: '/experience/sfoglia-tortellini-experience' },
    { label: i18n.language === 'it' ? 'Tour Privato Minibus' : 'Private Minibus Tour', to: i18n.language === 'it' ? '/noleggio-privato-minibus-bologna' : '/private-tour-bologna-electric-minibus', highlight: true },
    { label: i18n.language === 'it' ? 'Prenota ora' : 'Book now', to: '/experiences', highlight: true },
    { label: i18n.language === 'it' ? 'Regala un\'esperienza' : 'Gift an experience', to: i18n.language === 'it' ? '/regala-esperienze-bologna' : '/gift-experiences-bologna', highlight: true }
  ];

  const infoLinks = [
    { label: i18n.language === 'it' ? 'Chi siamo' : 'About us', to: '/about' },
    { label: i18n.language === 'it' ? 'Contatti' : 'Contact', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Blog', to: '/blog' },
    { label: i18n.language === 'it' ? 'Prenotazioni' : 'Bookings', to: '/experiences' },
    { label: i18n.language === 'it' ? 'Politica cancellazioni' : 'Cancellation Policy', to: '/terms' }
  ];

  const legalLinks = [
    { label: 'Privacy Policy', to: '/privacy' },
    { label: 'Cookie Policy', to: '/cookies' },
    { label: i18n.language === 'it' ? 'Termini e Condizioni' : 'Terms & Conditions', to: '/terms' },
    { label: i18n.language === 'it' ? 'Rimborsi e Cancellazioni' : 'Refunds & Cancellations', to: '/refund' }
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
          {/* Column 1 - Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-serif font-bold text-white mb-1">
                City Red Bus
              </h3>
              <p className="text-brand-red font-medium text-sm tracking-wide">
                {i18n.language === 'it' ? 'Esperienze e tour a Bologna' : 'Experiences and tours in Bologna'}
              </p>
            </div>
            
            <div className="space-y-4 text-gray-400 text-sm">
              <div className="flex items-start gap-3">
                <BuildingIcon />
                <div>
                  <p className="text-white font-medium">City Red Bus S.r.l.</p>
                  <p>Via di Saliceto 3</p>
                  <p>40128 Bologna, Italia</p>
                  <p className="mt-1 text-gray-500">P.IVA / C.F. 02971591207</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <MailIcon />
                <a 
                  href="mailto:travel@cityredbus.com" 
                  className="hover:text-white transition-colors"
                >
                  travel@cityredbus.com
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <PhoneIcon />
                <a 
                  href="tel:+39051379452" 
                  className="hover:text-white transition-colors"
                >
                  +39 051 379452
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <SmartphoneIcon />
                <a 
                  href="tel:+393423977050" 
                  className="hover:text-white transition-colors"
                >
                  +39 342 397 7050
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a 
                href="https://www.instagram.com/scopribologna/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-brand-red rounded-lg flex items-center justify-center transition-all duration-300 group"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/cityredbus" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-brand-red rounded-lg flex items-center justify-center transition-all duration-300 group"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://wa.me/393423977050" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-green-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
            
            {/* City Red Bus Link */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">
                Bologna City Experience è un progetto
              </p>
              <a 
                href="https://www.cityredbus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white font-medium hover:text-brand-red transition-colors"
              >
                City Red Bus Bologna →
              </a>
            </div>
            
            {/* Google Reviews Badge */}
            <div className="mt-6">
              <Suspense fallback={<div className="h-20 bg-gray-800 rounded-xl animate-pulse" />}>
                <FooterReviewsBadge />
              </Suspense>
            </div>
          </div>

          {/* Column 2 - Esperienze */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              {i18n.language === 'it' ? 'Esperienze' : 'Experiences'}
            </h4>
            <ul className="space-y-3">
              {experienceLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={localizedPath(link.to)} 
                    className={`text-sm transition-colors duration-200 ${
                      link.highlight 
                        ? 'text-brand-red hover:text-white font-medium' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {link.highlight && '→ '}{link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Informazioni */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              {i18n.language === 'it' ? 'Informazioni' : 'Information'}
            </h4>
            <ul className="space-y-3">
              {infoLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={localizedPath(link.to)} 
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Legale */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              {i18n.language === 'it' ? 'Legale' : 'Legal'}
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => (
                <li key={i}>
                  <Link 
                    to={localizedPath(link.to)} 
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Bottom Bar */}
          <div className="py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Copyright */}
              <div className="text-gray-500 text-sm">
                <p>© 2026 City Red Bus S.r.l. – {i18n.language === 'it' ? 'Tutti i diritti riservati' : 'All rights reserved'}</p>
                <p className="text-gray-600 text-xs mt-1">
                  {i18n.language === 'it' ? 'Sede legale' : 'Registered office'}: Via di Saliceto 3, 40128 Bologna, Italia – P.IVA/C.F. 02971591207
                </p>
              </div>

              {/* Legal Links */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <Link to={localizedPath('/privacy')} className="text-gray-500 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-gray-700 hidden sm:inline">|</span>
                <Link to={localizedPath('/cookies')} className="text-gray-500 hover:text-white transition-colors">
                  Cookie Policy
                </Link>
                <span className="text-gray-700 hidden sm:inline">|</span>
                <Link to={localizedPath('/terms')} className="text-gray-500 hover:text-white transition-colors">
                  {i18n.language === 'it' ? 'Termini e Condizioni' : 'Terms & Conditions'}
                </Link>
                <span className="text-gray-700 hidden sm:inline">|</span>
                <button 
                  onClick={() => {/* Cookie preferences modal */}}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  {i18n.language === 'it' ? 'Preferenze Cookie' : 'Cookie Preferences'}
                </button>
                <span className="text-gray-700 hidden sm:inline">|</span>
                <Link to="/admin/login" className="text-gray-500 hover:text-white transition-colors">
                  {i18n.language === 'it' ? 'Area Riservata' : 'Admin Area'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
